'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, DropdownMenu, Input, InputDecorator, Spinner } from '@common/components';
import { FOCUS_RING } from '@common/components/interaction';
import { useDebouncedValue, useToast } from '@common/hooks';
import SvgRefresh from '@common/icon/SvgRefresh';
import SvgSearch from '@common/icon/SvgSearch';
import { cn } from '@core/utils';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { KeyboardEvent } from 'react';
import { useId, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useInView } from 'react-intersection-observer';
import { isDonationPath } from '@/utils/isDonationPath';
import { CHANNEL_LABEL, toOptions } from '../../_utils/labels';
import type { LinkFormValue, LinkTarget, SearchType } from '../../_utils/links';
import { eventTargetOf, filterTargets, giftTargetOf, pageTargetOf, regionTargetOf, targetDetailsOf, targetViewUrlOf, toCreateRequest } from '../../_utils/links';
import type { PartnersQueries } from '../../_utils/queries';
import { contextOf } from '../../_utils/queries';
import type { Channel, CreatePartnersLinkRequest } from '../../_utils/types';
import { ChannelOptionIcon } from './ChannelOptionIcon';
import { choiceOptionsOf, EMPTY_FORM, SEARCH_LABEL, searchTypeOf } from './formValue';
const CHANNEL_OPTIONS = toOptions(CHANNEL_LABEL).map(option => ({
  ...option,
  icon: <ChannelOptionIcon channel={option.value} />
}));
const SEARCH_DEBOUNCE_MS = 200;
const RESULT_MESSAGE_CLASS_NAME = 'py-12 text-center text-Gray-600 typo-caption1';
interface TargetSearchProps {
  queries: PartnersQueries;
  searchType: SearchType;
  query: string;
  target: LinkTarget | null;
  onQueryChange: (query: string) => void;
  onReset: () => void;
  onSelect: (target: LinkTarget) => void;
}
const TargetSearch = t0 => {
  const $ = _c(64);
  const {
    queries,
    searchType,
    query,
    target,
    onQueryChange,
    onReset,
    onSelect
  } = t0;
  const listId = useId();
  const [activeIndex, setActiveIndex] = useState(-1);
  let t1;
  if ($[0] !== query) {
    t1 = query.trim();
    $[0] = query;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const keyword = useDebouncedValue(t1, SEARCH_DEBOUNCE_MS);
  const isSearching = target === null;
  let t2;
  if ($[2] !== keyword || $[3] !== queries) {
    t2 = queries.giftProducts(keyword);
    $[2] = keyword;
    $[3] = queries;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = isSearching && searchType === "GIFT_PRODUCT";
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = {
      ...t2,
      select: _temp3,
      enabled: t3,
      placeholderData: keepPreviousData
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const giftResults = useInfiniteQuery(t4);
  let t5;
  if ($[8] !== queries) {
    t5 = queries.regions();
    $[8] = queries;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== keyword) {
    t6 = response_0 => filterTargets(contextOf(response_0).contents.map(regionTargetOf), keyword);
    $[10] = keyword;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = isSearching && searchType === "REGION";
  let t8;
  if ($[12] !== t5 || $[13] !== t6 || $[14] !== t7) {
    t8 = {
      ...t5,
      select: t6,
      enabled: t7
    };
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const regionResults = useQuery(t8);
  let t9;
  if ($[16] !== queries) {
    t9 = queries.events();
    $[16] = queries;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== keyword) {
    t10 = response_1 => filterTargets(contextOf(response_1).contents.map(eventTargetOf), keyword);
    $[18] = keyword;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const t11 = isSearching && searchType === "MONTHLY_EVENT";
  let t12;
  if ($[20] !== t10 || $[21] !== t11 || $[22] !== t9) {
    t12 = {
      ...t9,
      select: t10,
      enabled: t11
    };
    $[20] = t10;
    $[21] = t11;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const eventResults = useQuery(t12);
  const {
    data: t13,
    isPending,
    isError
  } = {
    GIFT_PRODUCT: giftResults,
    REGION: regionResults,
    MONTHLY_EVENT: eventResults
  }[searchType];
  const results = t13 === undefined ? [] : t13;
  let t14;
  if ($[24] !== giftResults) {
    t14 = {
      onChange: inView => {
        if (inView && giftResults.hasNextPage && !giftResults.isFetchingNextPage) {
          giftResults.fetchNextPage();
        }
      }
    };
    $[24] = giftResults;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const {
    ref: loadMoreRef
  } = useInView(t14);
  let t15;
  if ($[26] !== listId) {
    t15 = index => `${listId}-${index}`;
    $[26] = listId;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const optionIdOf = t15;
  const activeResult = isSearching ? results[activeIndex] : undefined;
  let t16;
  if ($[28] !== optionIdOf) {
    t16 = nextIndex => {
      setActiveIndex(nextIndex);
      document.getElementById(optionIdOf(nextIndex))?.scrollIntoView({
        block: "nearest"
      });
    };
    $[28] = optionIdOf;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const moveActive = t16;
  const handleKeyDown = event => {
    if (!isSearching || results.length === 0) {
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveActive(Math.min(activeIndex + 1, results.length - 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveActive(Math.max(activeIndex - 1, 0));
      return;
    }
    if (event.key === "Enter" && activeResult && !event.nativeEvent.isComposing) {
      event.preventDefault();
      onSelect(activeResult);
    }
  };
  let t17;
  bb0: {
    if (isPending) {
      let t18;
      if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = <p aria-busy={true} className={cn(RESULT_MESSAGE_CLASS_NAME, "invisible")}>찾는 결과가 없어요</p>;
        $[30] = t18;
      } else {
        t18 = $[30];
      }
      t17 = t18;
      break bb0;
    }
    if (isError) {
      let t18;
      if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = <p className={RESULT_MESSAGE_CLASS_NAME}>검색 결과를 불러오지 못했어요. 잠시 후 다시 찾아 주세요.</p>;
        $[31] = t18;
      } else {
        t18 = $[31];
      }
      t17 = t18;
      break bb0;
    }
    if (results.length === 0) {
      let t18;
      if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = <p className={RESULT_MESSAGE_CLASS_NAME}>찾는 결과가 없어요</p>;
        $[32] = t18;
      } else {
        t18 = $[32];
      }
      t17 = t18;
      break bb0;
    }
    let t18;
    if ($[33] !== activeIndex || $[34] !== onSelect || $[35] !== optionIdOf) {
      t18 = (result, index_0) => <li key={result.destinationPath} role="presentation"><button type="button" id={optionIdOf(index_0)} role="option" aria-selected={index_0 === activeIndex} className={cn(FOCUS_RING, "flex w-full flex-col items-start gap-4 px-16 py-12 text-left transition-colors duration-150 active:bg-Gray-100 motion-reduce:transition-none", index_0 === activeIndex && "bg-Gray-50")} onPointerMove={() => setActiveIndex(index_0)} onClick={() => onSelect(result)}><span className="text-Gray-900 typo-body4">{result.targetName}</span><span className="text-Gray-600 typo-caption1">{targetDetailsOf(result).join(" \xB7 ")}</span></button></li>;
      $[33] = activeIndex;
      $[34] = onSelect;
      $[35] = optionIdOf;
      $[36] = t18;
    } else {
      t18 = $[36];
    }
    let t19;
    if ($[37] !== giftResults || $[38] !== loadMoreRef || $[39] !== searchType) {
      t19 = searchType === "GIFT_PRODUCT" && giftResults.hasNextPage ? <li ref={loadMoreRef} role="presentation" className="flex shrink-0 justify-center py-8">{giftResults.isFetchingNextPage ? <Spinner size={24} /> : null}</li> : null;
      $[37] = giftResults;
      $[38] = loadMoreRef;
      $[39] = searchType;
      $[40] = t19;
    } else {
      t19 = $[40];
    }
    t17 = <ul id={listId} role="listbox" className="flex max-h-280 flex-col overflow-y-auto rounded-xsmall border border-Gray-200">{results.map(t18)}{t19}</ul>;
  }
  const resultsView = t17;
  const t18 = SEARCH_LABEL[searchType];
  const t19 = isSearching && results.length > 0;
  const t20 = activeResult ? optionIdOf(activeIndex) : undefined;
  const t21 = `${SEARCH_LABEL[searchType]} 이름으로 찾아보세요.`;
  const t22 = `${SEARCH_LABEL[searchType]} 다시 찾기`;
  let t23;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = cn(FOCUS_RING, "flex rounded-xxsmall transition-opacity enabled:hover:opacity-80 enabled:active:opacity-70");
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <SvgSearch aria-hidden={true} originalFill={true} className="size-16 text-Gray-700" />;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== isSearching || $[44] !== onReset || $[45] !== t22) {
    t25 = <button type="button" aria-label={t22} disabled={isSearching} className={t23} onClick={onReset}>{t24}</button>;
    $[43] = isSearching;
    $[44] = onReset;
    $[45] = t22;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== onQueryChange) {
    t26 = event_0 => {
      setActiveIndex(-1);
      onQueryChange(event_0.target.value);
    };
    $[47] = onQueryChange;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== handleKeyDown || $[50] !== listId || $[51] !== query || $[52] !== t19 || $[53] !== t20 || $[54] !== t21 || $[55] !== t25 || $[56] !== t26) {
    t27 = <Input value={query} role="combobox" aria-expanded={t19} aria-controls={listId} aria-autocomplete="list" aria-activedescendant={t20} placeholder={t21} rightContent={t25} onKeyDown={handleKeyDown} onChange={t26} />;
    $[49] = handleKeyDown;
    $[50] = listId;
    $[51] = query;
    $[52] = t19;
    $[53] = t20;
    $[54] = t21;
    $[55] = t25;
    $[56] = t26;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== t18 || $[59] !== t27) {
    t28 = <InputDecorator label={t18}>{t27}</InputDecorator>;
    $[58] = t18;
    $[59] = t27;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  const t29 = isSearching ? resultsView : null;
  let t30;
  if ($[61] !== t28 || $[62] !== t29) {
    t30 = <div className="flex flex-col gap-8">{t28}{t29}</div>;
    $[61] = t28;
    $[62] = t29;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  return t30;
};
export interface CreateCallbacks {
  onSuccess: () => void;
  onSettled: () => void;
}
interface LinkFormProps {
  queries: PartnersQueries;
  initialValue: LinkFormValue;
  isSuspended: boolean;
  isCreating: boolean;
  onCreate: (request: CreatePartnersLinkRequest, callbacks: CreateCallbacks) => void;
  note?: string;
}
export const LinkForm = t0 => {
  const $ = _c(87);
  const {
    queries,
    initialValue,
    isSuspended,
    isCreating,
    onCreate,
    note
  } = t0;
  const {
    toast
  } = useToast();
  const isSubmittingRef = useRef(false);
  let t1;
  if ($[0] !== initialValue) {
    t1 = {
      defaultValues: initialValue
    };
    $[0] = initialValue;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    control,
    setValue,
    reset,
    handleSubmit
  } = useForm(t1);
  let t2;
  if ($[2] !== queries) {
    t2 = queries.linkPages();
    $[2] = queries;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = {
      ...t2,
      select: _temp4
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: t4
  } = useQuery(t3);
  let t5;
  if ($[6] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const pages = t5;
  let t6;
  if ($[8] !== control) {
    t6 = {
      control,
      name: "choice"
    };
    $[8] = control;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const choice = useWatch(t6);
  let t7;
  if ($[10] !== control) {
    t7 = {
      control,
      name: "query"
    };
    $[10] = control;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const query = useWatch(t7);
  let t8;
  if ($[12] !== control) {
    t8 = {
      control,
      name: "target"
    };
    $[12] = control;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const target = useWatch(t8);
  let t9;
  if ($[14] !== control) {
    t9 = {
      control,
      name: "channel"
    };
    $[14] = control;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const channel = useWatch(t9);
  let t10;
  if ($[16] !== control) {
    t10 = {
      control,
      name: "name"
    };
    $[16] = control;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const name = useWatch(t10);
  let t11;
  if ($[18] !== choice) {
    t11 = searchTypeOf(choice);
    $[18] = choice;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const searchType = t11;
  let t12;
  if ($[20] !== channel || $[21] !== isSuspended || $[22] !== name || $[23] !== target) {
    t12 = !isSuspended && target !== null && channel !== "" && name.trim() !== "";
    $[20] = channel;
    $[21] = isSuspended;
    $[22] = name;
    $[23] = target;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const canSubmit = t12;
  let t13;
  if ($[25] !== onCreate || $[26] !== reset || $[27] !== toast) {
    t13 = value => {
      if (!value.target || isSubmittingRef.current) {
        return;
      }
      if (!isDonationPath(value.target.destinationPath)) {
        toast({
          type: "fail",
          message: "\uACE0\uD5A5\uC0AC\uB791\uAE30\uBD80 \uD398\uC774\uC9C0\uB9CC \uC5F0\uACB0\uD560 \uC218 \uC788\uC5B4\uC694"
        });
        return;
      }
      isSubmittingRef.current = true;
      onCreate(toCreateRequest({
        ...value,
        target: value.target
      }), {
        onSuccess: () => reset(EMPTY_FORM),
        onSettled: () => {
          isSubmittingRef.current = false;
        }
      });
    };
    $[25] = onCreate;
    $[26] = reset;
    $[27] = toast;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const createWith = t13;
  let t14;
  if ($[29] !== createWith || $[30] !== handleSubmit) {
    t14 = event => handleSubmit(createWith)(event);
    $[29] = createWith;
    $[30] = handleSubmit;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== pages) {
    t15 = choiceOptionsOf(pages);
    $[32] = pages;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== pages || $[35] !== setValue) {
    t16 = next => {
      setValue("choice", next);
      setValue("query", "");
      const page = pages.find(item => item.partnersLinkPageId === next);
      setValue("target", page ? pageTargetOf(page) : null);
    };
    $[34] = pages;
    $[35] = setValue;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== choice || $[38] !== t15 || $[39] !== t16) {
    t17 = <DropdownMenu options={t15} value={choice} placeholder={"\uC5B4\uB514\uB85C \uC5F0\uACB0\uD560\uAE4C\uC694?"} className="min-w-0 flex-1" onChange={t16} />;
    $[37] = choice;
    $[38] = t15;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== target) {
    t18 = target ? <Button asChild={true} theme="sub-line" dimensions="H48" className="shrink-0"><a href={targetViewUrlOf(target)} target="_blank" rel="noreferrer">보러가기</a></Button> : null;
    $[41] = target;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t17 || $[44] !== t18) {
    t19 = <InputDecorator label={"\uC774\uB3D9\uD560 \uD398\uC774\uC9C0"}><div className="flex gap-8">{t17}{t18}</div></InputDecorator>;
    $[43] = t17;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== queries || $[47] !== query || $[48] !== searchType || $[49] !== setValue || $[50] !== target) {
    t20 = searchType ? <TargetSearch key={searchType} queries={queries} searchType={searchType} query={query} target={target} onQueryChange={next_0 => {
      setValue("query", next_0);
      setValue("target", null);
    }} onReset={() => {
      setValue("query", "");
      setValue("target", null);
    }} onSelect={result => {
      setValue("target", result);
      setValue("query", result.targetName);
    }} /> : null;
    $[46] = queries;
    $[47] = query;
    $[48] = searchType;
    $[49] = setValue;
    $[50] = target;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t19 || $[53] !== t20) {
    t21 = <div className="flex flex-col gap-12">{t19}{t20}</div>;
    $[52] = t19;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== setValue) {
    t22 = next_1 => setValue("channel", next_1);
    $[55] = setValue;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== channel || $[58] !== t22) {
    t23 = <InputDecorator label={"\uAC8C\uC2DC\uD560 \uCC44\uB110"}><DropdownMenu options={CHANNEL_OPTIONS} value={channel} placeholder={"\uB9C1\uD06C\uB97C \uAC8C\uC2DC\uD560 \uCC44\uB110\uC744 \uC120\uD0DD\uD574\uC8FC\uC138\uC694."} className="w-full" onChange={t22} /></InputDecorator>;
    $[57] = channel;
    $[58] = t22;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== setValue) {
    t24 = event_0 => setValue("name", event_0.target.value);
    $[60] = setValue;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== name || $[63] !== t24) {
    t25 = <InputDecorator label={"\uCF58\uD150\uCE20\uBA85"}><Input maxLength={30} placeholder={"\uCF58\uD150\uCE20\uBA85\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694."} value={name} onChange={t24} /></InputDecorator>;
    $[62] = name;
    $[63] = t24;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== t21 || $[66] !== t23 || $[67] !== t25) {
    t26 = <div className="flex flex-col gap-16">{t21}{t23}{t25}</div>;
    $[65] = t21;
    $[66] = t23;
    $[67] = t25;
    $[68] = t26;
  } else {
    t26 = $[68];
  }
  let t27;
  if ($[69] !== reset) {
    t27 = () => reset(EMPTY_FORM);
    $[69] = reset;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <SvgRefresh aria-hidden={true} className="size-16" />;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  let t29;
  if ($[72] !== t27) {
    t29 = <div className="w-114 shrink-0 xl:w-139"><Button theme="sub-line" dimensions="H48" className="w-full" onClick={t27}>{t28}초기화</Button></div>;
    $[72] = t27;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  const t30 = !canSubmit;
  let t31;
  if ($[74] !== isCreating || $[75] !== t30) {
    t31 = <Button type="submit" dimensions="H48" className="flex-1" disabled={t30} loading={isCreating}>링크만들기</Button>;
    $[74] = isCreating;
    $[75] = t30;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  let t32;
  if ($[77] !== t29 || $[78] !== t31) {
    t32 = <div className="flex gap-8">{t29}{t31}</div>;
    $[77] = t29;
    $[78] = t31;
    $[79] = t32;
  } else {
    t32 = $[79];
  }
  let t33;
  if ($[80] !== note) {
    t33 = note ? <p className="text-center text-Gray-600 typo-caption1">{note}</p> : null;
    $[80] = note;
    $[81] = t33;
  } else {
    t33 = $[81];
  }
  let t34;
  if ($[82] !== t14 || $[83] !== t26 || $[84] !== t32 || $[85] !== t33) {
    t34 = <form className="flex flex-col gap-24 rounded-medium border border-Gray-200 bg-Gray-white p-16 shadow-base xl:p-20" onSubmit={t14}>{t26}{t32}{t33}</form>;
    $[82] = t14;
    $[83] = t26;
    $[84] = t32;
    $[85] = t33;
    $[86] = t34;
  } else {
    t34 = $[86];
  }
  return t34;
};
function _temp(gift) {
  return giftTargetOf(gift) ?? [];
}
function _temp2(page) {
  return (contextOf(page).contents ?? []).flatMap(_temp);
}
function _temp3(response) {
  return response.pages.flatMap(_temp2);
}
function _temp4(response) {
  return contextOf(response).contents;
}